import { materialTypeFormat } from '@/common/material-type-formatting';
import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { GetAvailableFacilitiesQuery, useGetAvailableFacilitiesQuery } from './graphql.generated';

// Define the shape of your context state
export interface FacilityContextValue {
	availableFacilities: GetAvailableFacilitiesQuery['facilities'];
	loadingFacilities: boolean;
	currentFacility: SelectedFacility | null;
	selectFacility: (facilityId: string) => void;
	availableProductTypes: {
		name: string;
		value: string;
		emoji: string;
	}[];
}

type SelectedFacility = NonNullable<NonNullable<GetAvailableFacilitiesQuery['facilities']>[number]>;

// Create the context with undefined as default
// eslint-disable-next-line react-refresh/only-export-components
export const FacilityContext = createContext<FacilityContextValue | undefined>(undefined);

// Provider component props
interface FacilityContextProviderProps {
	children: ReactNode;
}

// Provider component
export const FacilityContextProvider: React.FC<FacilityContextProviderProps> = ({ children }) => {
	const { data: facilitiesData, loading: loadingFacilities } = useGetAvailableFacilitiesQuery();
	const [currentFacility, setCurrentFacility] = useState<SelectedFacility | null>(null);
	const availableFacilities = useMemo(() => {
		return facilitiesData?.facilities ?? [];
	}, [facilitiesData]);

	const selectFacility = useCallback(
		(facilityId: string) => {
			if (availableFacilities.length === 0) {
				return;
			}

			const selection = availableFacilities.find((facility) => facility?.id === facilityId);

			if (!selection) {
				// TO DO: Handle this error with some kind of feedback to the user
				return;
			}

			if (currentFacility?.id === selection.id) {
				// If user selects the same facility, do nothing
				return;
			}
			setCurrentFacility(selection);

			// Save this to local storage to persist selection across sessions
			localStorage.setItem('currentFacility', selection.id);
		},
		[availableFacilities, currentFacility]
	);

	const availableProductTypes = useMemo(() => {
		const types = currentFacility?.productTypes ?? [];
		return Object.entries(materialTypeFormat)
			.filter(([materialType]) => {
				if (types.some((type) => type.name === materialType)) {
					return true;
				}
				return false;
			})
			.map(([materialType, config]) => {
				return {
					name: config.label,
					value: materialType,
					emoji: config?.emoji ?? '🔍',
				};
			});
	}, [currentFacility]);

	useEffect(() => {
		const storedFacilityId = localStorage.getItem('currentFacility');
		if (storedFacilityId && availableFacilities.length > 0) {
			if (!availableFacilities.find((facility) => facility?.id === storedFacilityId)) {
				localStorage.removeItem('currentFacility');
				return;
			} else {
				selectFacility(storedFacilityId);
			}
		} else if (availableFacilities.length === 1) {
			selectFacility(availableFacilities[0]!.id);
		}
	}, [availableFacilities, selectFacility]);

	return (
		<FacilityContext.Provider
			value={{
				availableFacilities,
				loadingFacilities,
				currentFacility,
				selectFacility,
				availableProductTypes,
			}}
		>
			{children}
		</FacilityContext.Provider>
	);
};
